import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';
import Nav from './Nav';
import EdgeSentence from './EdgeSentence';
import RelationField from './RelationField';
import { Explain } from './Explain';
import { relationColor, fmtScore } from './trustShared';
import { useLensWeights, recombine } from './lensWeights';
import { usePins, useTrail, togglePin, unpin, clearPins } from './workingSet';
import './css/trust.css';

// THE PAIR PAGE (records/RELATION_DRAWING_PREP.md R1, built 2026-08-05).
// /triage was already a pair page that did not know it was one — its whole job
// is "here is a pair, what relation belongs between them?", hardcoded to one
// input source (related_to rows) and one proposal source (the model). This
// GENERALIZES it rather than shipping a workbench beside it: a second listing
// of the same job is the project's named failure mode (the tree's guardrail,
// already violated once by ContextCards).
//
// Two pair sources, deliberately unranked:
//   1. THE WORKING SET (R2) — objects pinned while browsing, all pairs among
//      them. Ships first and alone because it is the only source with no
//      ranker dependency: retrieval measures ~1 good list in 5, and launching
//      on a retrieval-fed queue would make "this page is hard to use"
//      indistinguishable from "these pairs were not worth relating". Manual
//      means intentional.
//   2. DEFERRED PAIRS (R6) — the related_to queue, relabelled as what it is:
//      "I know these connect, I cannot say how yet". Same row written; the
//      interface stops presenting it as a reading. Saying the relation (the
//      field) is the primary gesture; the model proposes ON REQUEST only (R3).
//
// Position (R7): each pair object carries its immediate neighborhood — source
// document, drawn-relation counts, the viewer's blend — and the TRAIL of
// recently visited objects feeds the working set. Both client-side.

import { snip } from './snip';

// ——— one object's card inside a pair row, with its R7 neighborhood ———
function PairCard({ badge, obj, edges, blend }) {
  // neighborhood, from the object's own edges: where it came from, and what
  // has already been said about it
  const sourceDoc = (edges || []).find(
    (e) => e.relation_name === 'derived_from' && e.source_id === obj.id
  );
  const counts = {};
  for (const e of edges || []) {
    if (e.is_system) continue;
    counts[e.relation_name] = (counts[e.relation_name] || 0) + 1;
  }
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 4);
  return (
    <div className="wv-card" style={{ padding: 'var(--wv-sp-4) var(--wv-sp-5)' }}>
      <div style={{ display: 'flex', gap: 'var(--wv-sp-3)', alignItems: 'baseline' }}>
        <span className="wv-chip" aria-hidden="true" style={{ flexShrink: 0 }}>{badge}</span>
        <div style={{ minWidth: 0 }}>
          <Link to={`/object/${obj.id}`} style={{ color: 'inherit' }}>
            {snip(obj.content)}
          </Link>
          <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-1)', display: 'flex', gap: 'var(--wv-sp-4)', flexWrap: 'wrap' }}>
            <span>{obj.object_type}</span>
            {typeof blend === 'number' && <span>blend {fmtScore(blend)}</span>}
            {sourceDoc && (
              <span>
                from{' '}
                <Link to={`/object/${sourceDoc.target_id}`}>
                  “{snip(sourceDoc.target_content, 50)}”
                </Link>
              </span>
            )}
            {top.length > 0 && (
              <span>
                {top.map(([name, n]) => (
                  <span key={name} style={{ marginRight: 'var(--wv-sp-2)', color: relationColor(name) }}>
                    {name.replace(/_/g, ' ')}×{n}
                  </span>
                ))}
              </span>
            )}
            {!sourceDoc && top.length === 0 && <span>no edges yet</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Triage() {
  const { user } = useAuth();
  const { weights } = useLensWeights();
  const pins = usePins();
  const trail = useTrail();

  // ——— deferred (related_to) queue ———
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState(null);
  const [rowState, setRowState] = useState({}); // id -> {loading, suggestions, error, applied}

  const loadQueue = useCallback(() => {
    apiFetch('/api/triage/related-to')
      .then(async (res) => {
        if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => setQueue(json.queue))
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // ——— working-set pair data: each pin's edges and scores ———
  const [edgesByObj, setEdgesByObj] = useState({}); // id -> edges[]
  const [scores, setScores] = useState({}); // id -> byLens
  const asked = useRef(new Set());

  const fetchEdgesFor = useCallback(async (ids) => {
    for (const oid of ids) {
      try {
        const res = await apiFetch(`/api/relation-instances/${oid}`);
        if (!res.ok) continue;
        const rows = await res.json();
        // merge keyed by id — a late response can never clobber another object
        setEdgesByObj((m) => ({ ...m, [oid]: rows }));
      } catch {
        /* leave the entry missing; a refetch can fill it */
      }
    }
  }, []);

  useEffect(() => {
    const missing = pins.map((p) => p.id).filter((oid) => !asked.current.has(oid));
    if (!missing.length) return;
    missing.forEach((oid) => asked.current.add(oid));
    fetchEdgesFor(missing);
    (async () => {
      try {
        const res = await apiFetch(`/api/claims/scores?ids=${missing.join(',')}`);
        if (!res.ok) return;
        const body = await res.json();
        setScores((s) => ({ ...s, ...body.scores }));
      } catch {}
    })();
  }, [pins, fetchEdgesFor]);

  // after a draw or a vote on a pair, that pair's two objects are stale
  const refreshPair = useCallback(
    (aId, bId) => {
      fetchEdgesFor([aId, bId]);
      loadQueue(); // a drawn related_to lands in the deferred queue; a specific relation clears it
    },
    [fetchEdgesFor, loadQueue]
  );

  // all unordered pin pairs, in pin order — deliberately NO ranking rule.
  // The working set is the manual source; ordering it would re-import the
  // ranker dependency this source exists to avoid.
  const pairs = useMemo(() => {
    const out = [];
    for (let i = 0; i < pins.length; i++)
      for (let j = i + 1; j < pins.length; j++) out.push([pins[i], pins[j]]);
    return out;
  }, [pins]);

  const patchRow = (id, patch) =>
    setRowState((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const requestSuggestions = async (id) => {
    patchRow(id, { loading: true, error: null });
    try {
      const res = await apiFetch(`/api/triage/related-to/${id}/suggest`, { method: 'POST' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      patchRow(id, {
        loading: false,
        suggestions: json.suggestions,
        error: json.suggestions.length ? null : 'No specific relation suggested — leave it deferred for now',
      });
    } catch (err) {
      patchRow(id, { loading: false, error: err.message });
    }
  };

  const accept = async (id, suggestion) => {
    patchRow(id, { loading: true, error: null });
    try {
      const res = await apiFetch(`/api/triage/related-to/${id}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          relation_name: suggestion.relation_name,
          polarity: suggestion.polarity,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      patchRow(id, { loading: false, applied: suggestion.relation_name });
    } catch (err) {
      patchRow(id, { loading: false, error: err.message });
    }
  };

  const pinnedIds = new Set(pins.map((p) => p.id));
  const trailUnpinned = trail.filter((t) => !pinnedIds.has(t.id));
  const blendOf = (oid) => (scores[oid] ? recombine(scores[oid].byLens, weights) : undefined);

  return (
    <div className="trust-page">
      <Nav />
      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '0 var(--wv-sp-8) 40px' }}>
        <h1 style={{ marginTop: 0 }}>
          <Explain
            title="The pair page"
            text="Here is a pair of objects — what relation belongs between them? Pairs come from your working set (objects you pinned while browsing) or from the deferred queue (pairs marked as connected without saying how). Type the relation you mean; phrasing it backwards swaps the endpoints visibly. A relation you draw carries your +1; anything the model proposes stays inert until a human votes."
          >
            Relate
          </Explain>
        </h1>

        {/* ——— source 1: the working set ——— */}
        <div className="trust-section" style={{ marginBottom: 'var(--wv-sp-6)' }}>
          <h2 style={{ marginTop: 0, fontSize: 'var(--wv-fs-xl)' }}>Working set</h2>
          <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-ui)' }}>
            Pin objects from their pages while you read; every pair of pins shows up here to
            relate. Client-side — your set, this browser.
          </p>

          {pins.length > 0 && (
            <div style={{ display: 'flex', gap: 'var(--wv-sp-2)', flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--wv-sp-4)' }}>
              {pins.map((p) => (
                <span key={p.id} className="wv-chip" style={{ display: 'inline-flex', gap: 'var(--wv-sp-2)', alignItems: 'center' }}>
                  <Link to={`/object/${p.id}`} style={{ color: 'inherit' }}>{snip(p.content, 60)}</Link>
                  <button
                    onClick={() => unpin(p.id)}
                    aria-label={`Unpin “${snip(p.content, 40)}”`}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--wv-muted)', padding: 0 }}
                  >
                    ×
                  </button>
                </span>
              ))}
              <button onClick={clearPins} style={{ fontSize: 'var(--wv-fs-xs)' }}>clear all</button>
            </div>
          )}

          {trailUnpinned.length > 0 && (
            <div style={{ marginBottom: 'var(--wv-sp-4)' }}>
              <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)', marginBottom: 'var(--wv-sp-2)' }}>
                recently visited — your trail through the graph; pin one to pair it
              </div>
              <div style={{ display: 'flex', gap: 'var(--wv-sp-2)', flexWrap: 'wrap' }}>
                {trailUnpinned.slice(0, 10).map((t) => (
                  <span key={t.id} className="wv-chip" style={{ display: 'inline-flex', gap: 'var(--wv-sp-2)', alignItems: 'center', opacity: 0.85, maxWidth: '100%' }}>
                    <Link to={`/object/${t.id}`} style={{ color: 'inherit', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{snip(t.content, 50)}</Link>
                    {user && (
                      <button
                        onClick={() => togglePin(t)}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--wv-muted)', padding: 0, fontSize: 'var(--wv-fs-xs)' }}
                      >
                        pin
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {pins.length < 2 && (
            <p className="trust-muted">
              {pins.length === 0
                ? 'Nothing pinned yet. Open any object and press “pin”; with two or more pins, their pairs appear here.'
                : 'One pin — one more and the pair appears here.'}
            </p>
          )}

          {pairs.map(([a, b]) => {
            const aEdges = edgesByObj[a.id];
            // every edge that runs directly between the two, either direction
            const between = (aEdges || []).filter(
              (e) =>
                !e.is_system &&
                ((e.source_id === a.id && e.target_id === b.id) ||
                  (e.source_id === b.id && e.target_id === a.id))
            );
            return (
              <div key={`${a.id}|${b.id}`} className="wv-panel" style={{ marginBottom: 'var(--wv-sp-5)', backgroundColor: 'var(--wv-surface2)' }}>
                <PairCard badge="A" obj={a} edges={edgesByObj[a.id]} blend={blendOf(a.id)} />
                <div style={{ margin: 'var(--wv-sp-3) 0', paddingLeft: 'var(--wv-sp-6)' }}>
                  {between.map((e) => (
                    <EdgeSentence key={e.id} edge={e} user={user} onVoted={() => refreshPair(a.id, b.id)} onRetyped={() => refreshPair(a.id, b.id)} />
                  ))}
                  {user && (
                    <div style={{ marginTop: between.length ? 'var(--wv-sp-2)' : 0 }}>
                      <RelationField
                        a={{ id: a.id, type: a.object_type }}
                        b={{ id: b.id, type: b.object_type }}
                        aLabel="A"
                        bLabel="B"
                        user={user}
                        placeholder={
                          between.length
                            ? 'add another reading — “qualifies”, “restates”…'
                            : 'say the connection — “supports”, “is refuted by”, “answers”…'
                        }
                        onDrawn={() => refreshPair(a.id, b.id)}
                      />
                    </div>
                  )}
                </div>
                <PairCard badge="B" obj={b} edges={edgesByObj[b.id]} blend={blendOf(b.id)} />
              </div>
            );
          })}
        </div>

        {/* ——— source 2: deferred pairs (the related_to queue) ——— */}
        <div className="trust-section">
          <h2 style={{ marginTop: 0, fontSize: 'var(--wv-fs-xl)' }}>
            <Explain
              title="Deferred pairs"
              text="related_to is a deferral, not a reading — it says “I know these connect, I cannot say how yet”, and writing it is what puts a pair in this queue. Say the relation yourself (your +1 activates it), or ask the model for a proposal — accepted proposals stay inert until someone votes. The related_to edge itself stays; votes decide which reading survives."
            >
              Deferred pairs
            </Explain>
          </h2>
          <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-ui)' }}>
            Pairs marked as connected without saying how. Saying it is the gesture; the model
            proposes only when asked.
          </p>

          {error && <div style={{ color: 'var(--wv-neg)' }}>{error}</div>}
          {!queue && !error && <p>Loading…</p>}
          {queue && queue.length === 0 && (
            <p className="trust-muted">Queue is empty — no pair is stuck at “connected, unsaid how”.</p>
          )}

          {(queue || []).map((row) => {
            const state = rowState[row.id] || {};
            return (
              <div key={row.id} className="trust-section" style={{ marginBottom: 'var(--wv-sp-4)' }}>
                <EdgeSentence
                  edge={{
                    ...row,
                    relation_name: 'related_to',
                    source_id: row.source,
                    target_id: row.target,
                  }}
                  onVoted={loadQueue}
                  onRetyped={loadQueue}
                  user={user}
                />

                {state.applied ? (
                  <div style={{ color: 'var(--wv-pos)', fontSize: 'var(--wv-fs-md)' }}>
                    ✓ reclassified as <strong>{state.applied}</strong> — inert until voted
                  </div>
                ) : (
                  <>
                    {user && (
                      <div style={{ margin: 'var(--wv-sp-2) 0' }}>
                        <RelationField
                          a={{ id: row.source, type: row.source_type }}
                          b={{ id: row.target, type: row.target_type }}
                          aLabel="the first"
                          bLabel="the second"
                          user={user}
                          placeholder="say what it actually is — “supports”, “is refuted by”…"
                          onDrawn={loadQueue}
                        />
                      </div>
                    )}
                    {!state.suggestions && (
                      <button onClick={() => requestSuggestions(row.id)} disabled={state.loading}>
                        {state.loading ? 'Asking…' : 'Suggest specific relation'}
                      </button>
                    )}
                    {(state.suggestions || []).map((s, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-4)', marginTop: 'var(--wv-sp-2)', flexWrap: 'wrap' }}>
                        <span className="relation-pill" style={{ background: relationColor(s.relation_name), margin: 0 }}>
                          {s.relation_name}
                          {s.polarity === -1 ? ' (neg)' : ''}
                        </span>
                        <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>{s.reason}</span>
                        <button onClick={() => accept(row.id, s)} disabled={state.loading} style={{ fontSize: 'var(--wv-fs-sm)' }}>
                          Accept
                        </button>
                      </div>
                    ))}
                    {state.error && (
                      <div style={{ color: 'var(--wv-warn)', fontSize: 'var(--wv-fs-sm)', marginTop: 'var(--wv-sp-2)' }}>{state.error}</div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Triage;
